import React, {useState} from 'react';
import {useQuery} from '@apollo/client';
import {ArrowLeftOutlined, CameraOutlined, CloseOutlined} from '@ant-design/icons';
import {Button, Col, Drawer, Empty, Image, Input, Popover, Row, Spin, Tag, message} from 'antd';
import {useTranslation} from 'react-i18next';
import {HasResource} from '../../../../components/HasResource';
import {ResourceCode} from '../../../../constants/resourceCodes';
import {normalizeLang, pickDisplayText} from '../../../../utils/i18nText';
import dayjs from 'dayjs';
import {formatEpochToDateTime, timestampToDate} from '../../../../utils/dateTimeUtils';
import {usePickupFailureReasonText} from '../hooks/usePickupFailureReasonText';
import {
    PARCEL_UNIT_I18N,
    PARTIAL_REASON_I18N
} from '../../../pickup/progress/components/PartialPickupAppointmentDrawer';
import {
    GET_POP_REVIEW_DETAIL_BY_ID,
    GetPopReviewDetailByIdResponse
} from '../graphql/queries/getPopReviewDetailById';
import {
    PopDisqualifyReasonOption,
    PopPickupResult,
    PopReviewAction,
    PopReviewScanRecord,
    PopReviewScene,
    PopReviewStatus,
    getPopPickupResult,
    isPopReviewFinished
} from '../graphql/types/PopReviewType';
import {usePopReviewActions} from '../hooks/usePopReviewActions';
import type {ReasonOptionGroup} from '../hooks/usePopReviewOptions';
import PopReviewRejectPopover from './PopReviewRejectPopover';
import {STATUS_COLOR} from './PopReviewTable';

/**
 * 审核按钮公共尺寸：宽度随文案自适应 —— 中英西三语文案长度差异大，
 * 固定宽度会让长语种文本溢出按钮边框。
 */
const REVIEW_BUTTON_BASE = 'h-auto min-h-8 min-w-24 px-5 whitespace-normal leading-tight';

/**
 * 已审单的结论按钮样式：命中当前结论的一侧加粗边框高亮，另一侧置灰。
 *
 * 对齐 podReview 的「按钮高亮表达结论」，使详情页一眼可见合格还是不合格；
 * 按钮为禁用态，重新提交结论走「改判」入口。
 * 「已取消」既非合格也非不合格，两侧都不高亮。
 */
const finishedButtonClass = (highlighted: boolean, isApprove: boolean): string => {
    if (!highlighted) {
        return `${REVIEW_BUTTON_BASE} !border-gray-200 !bg-gray-100 !text-gray-400`;
    }
    return isApprove
        ? `${REVIEW_BUTTON_BASE} !border-2 !border-green-500 !bg-green-50 !text-green-600`
        : `${REVIEW_BUTTON_BASE} !border-2 !border-red-500 !bg-red-50 !text-red-500`;
};

/**
 * 待审/改判态的结论按钮样式（对齐 podReview）：默认中性灰，仅悬浮透出绿/红，
 * 选中态（确认浮窗打开中，或改判时的当前结论）实底高亮。
 */
const verdictButtonClass = (isApprove: boolean, selected: boolean): string => {
    if (selected) {
        return isApprove
            ? `${REVIEW_BUTTON_BASE} !text-green-700 !bg-green-50 !border-green-500 hover:!bg-green-100`
            : `${REVIEW_BUTTON_BASE} !text-red-700 !bg-red-50 !border-red-500 hover:!bg-red-100`;
    }
    return isApprove
        ? `${REVIEW_BUTTON_BASE} !text-gray-600 !bg-white !border-gray-300 hover:!text-green-700 hover:!bg-green-50 hover:!border-green-500`
        : `${REVIEW_BUTTON_BASE} !text-gray-600 !bg-white !border-gray-300 hover:!text-red-700 hover:!bg-red-50 hover:!border-red-500`;
};

/** 扫码运单号默认展示条数，其余在弹窗里看 */
const SCAN_PREVIEW_COUNT = 4;

/** 栏内的「标签：值」行；标签列定宽以保证同栏各行的值左边缘对齐 */
const LabeledText: React.FC<{label: string; value?: React.ReactNode}> = ({label, value}) => (
    <div className="flex text-sm leading-6 mb-2">
        {/* 标签左对齐 + 固定列宽：值的左边缘仍落在同一条线上 */}
        <span className="text-gray-500 flex-shrink-0 w-[104px] mr-3">{label}:</span>
        <span className="flex-1 min-w-0 break-words">
            {value === null || value === undefined || value === '' ? '—' : value}
        </span>
    </div>
);

/** 地址 + 邮编拼接；两者皆空返回 undefined 交由 LabeledText 兜底 */
const joinAddress = (address?: string, zipcode?: string): string | undefined =>
    [address, zipcode].filter(Boolean).join(' ') || undefined;

/** 联系人姓名 + 电话拼接为「Vivian (+1 9498876992)」；缺一项则只显示另一项 */
const joinContact = (name?: string, phone?: string): string | undefined => {
    if (name && phone) return `${name} (${phone})`;
    return name || phone || undefined;
};

/** 照片位尺寸：宽度随网格列拉伸、3:4，高度封顶 75vh */
const PHOTO_CELL_CLASS =
    'relative w-full aspect-[3/4] max-h-[75vh] border border-gray-300 rounded-lg overflow-hidden bg-white';

interface PopReviewDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    /**
     * 审核单 ID 列表：点行打开时只有该条，批量审核时为选中的 ID。
     * 逐条展示、审完自动跳下一条（对齐 POD，不是一次提交多条）。
     */
    reviewIds: string[];
    /** 单条审核成功后回调，供列表刷新 */
    onReviewed?: () => void;
    /** 当前场景的不合格原因，按分类分组 */
    reasonOptions: ReasonOptionGroup[];
    /** 原因码 → 当前语言文案，用于回显已选原因 */
    reasonTextByCode: Record<string, string>;
    /** 原因字典原始数据；查看场景据此按分类分组回显 */
    disqualifyReasons: PopDisqualifyReasonOption[];
    hubNameById: Record<string, string>;
    /** 站点 ID → 名称；未命中回落显示 ID */
    pickupSiteNameById: Record<string, string>;
    /** 司机 account ID → 姓名；未命中回落显示 ID */
    pickupDriverNameById: Record<string, string>;
}

/**
 * 分类配色：按严重度递减：信息虚假(红) > 信息不全(橙) > 信息不完整(蓝) > 其他(灰)。
 * 「信息虚假」用红色 —— 它是唯一指向主观造假的判定，需要更强的视觉权重。
 * 成功场景分类用中性色系区分，不与失败场景的严重度色阶混用。
 */
const CATEGORY_TAG_COLOR: Record<string, string> = {
    FALSE_INFO: 'red',
    INCOMPLETE_INFO: 'orange',
    INCOMPLETE_CONTENT: 'blue',
    PARTIAL_PICKUP_INVALID: 'gold',
    PICKUP_INVALID: 'orange',
    BOL_INVALID: 'geekblue',
    WAYBILL_INVALID: 'purple',
    OTHER: 'default'
};

/** 揽收状态徽标配色（原型：全部揽收绿 / 部分揽收橙 / 未揽收红） */
const PICKUP_RESULT_TAG_COLOR: Record<PopPickupResult, string> = {
    [PopPickupResult.ALL]: 'green',
    [PopPickupResult.PARTIAL]: 'orange',
    [PopPickupResult.NONE]: 'red'
};

interface ReasonGroup {
    categoryCode: string;
    categoryLabel: string;
    reasons: {code: string; label: string}[];
}

/** 按分类分组展示不合格原因 */
const GroupedReasons: React.FC<{groups: ReasonGroup[]}> = ({groups}) => (
    <>
        {groups.map(group => (
            <div key={group.categoryCode} className="mb-1.5 last:mb-0">
                <div className="text-xs text-gray-400">{group.categoryLabel}</div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                    {group.reasons.map(r => (
                        <Tag key={r.code} color={CATEGORY_TAG_COLOR[group.categoryCode]}>
                            {r.label}
                        </Tag>
                    ))}
                </div>
            </div>
        ))}
    </>
);

const PopReviewDetailDrawer: React.FC<PopReviewDetailDrawerProps> = ({
    open,
    onClose,
    reviewIds,
    onReviewed,
    reasonOptions,
    reasonTextByCode,
    disqualifyReasons,
    hubNameById,
    pickupSiteNameById,
    pickupDriverNameById
}) => {
    const {t, i18n} = useTranslation();
    const getPickupFailureReasonText = usePickupFailureReasonText();

    // 传入列表变化时在渲染期重置索引，避免首帧沿用旧索引多查一次错误的单
    const [cursor, setCursor] = useState({ids: reviewIds, index: 0});
    if (cursor.ids !== reviewIds) {
        setCursor({ids: reviewIds, index: 0});
    }
    const currentIndex = cursor.ids === reviewIds ? cursor.index : 0;
    const setCurrentIndex = (index: number) => setCursor({ids: reviewIds, index});

    const queryId = reviewIds[currentIndex];
    // 对齐 POD：多于一条才是批量模式，只能往后跳过，不能回上一单
    const isBatchMode = reviewIds.length > 1;
    const hasNext = currentIndex + 1 < reviewIds.length;
    const showNoMore = isBatchMode && !hasNext;

    const {data, loading, refetch} = useQuery<GetPopReviewDetailByIdResponse>(
        GET_POP_REVIEW_DETAIL_BY_ID,
        {
            variables: {reviewTaskId: queryId},
            skip: !open || !queryId,
            fetchPolicy: 'network-only'
        }
    );

    // 撞上「已被他人审核」乐观锁时刷新当前单并退出改判态，展示他人的结论（按钮随之置灰、浮窗随 popKey 重置关闭）
    const {handleReview, loading: submitting} = usePopReviewActions(() => {
        setRevisingId(null);
        void refetch();
    });

    const detail = data?.getPopReviewDetailById;
    const item = detail?.item;
    const finished = isPopReviewFinished(item?.status);
    // 已审单用于高亮结论按钮；「已取消」两者皆 false，两侧按钮均置灰
    const approved = item?.status === PopReviewStatus.APPROVED;
    const rejected = item?.status === PopReviewStatus.REJECTED;
    // 改判态按单记录：切单即自动退出改判，避免把上一单的改判态带到下一单
    const [revisingId, setRevisingId] = useState<string | null>(null);
    const revising = revisingId === queryId;
    // 仅有明确结论的单可改判（「已取消」后端状态条件也不放行）；
    // 批量审核不提供改判入口 —— 改判是针对单条结论的复核动作
    const revisable = (approved || rejected) && !isBatchMode;

    // 合格确认 / 不合格原因两个浮窗的开合状态：集中受控并显式互斥（打开一个即关另一个），
    // 不依赖「点击浮层外自动收起」—— podReview 曾因浮层形态状态未显式切换出过不切换的 bug。
    // key 绑定当前单与是否改判，切单或进出改判模式时在渲染期重置，浮窗不跨单残留
    const popKey = `${queryId}-${revising}`;
    const [popState, setPopState] = useState({key: popKey, approve: false, reject: false});
    if (popState.key !== popKey) {
        setPopState({key: popKey, approve: false, reject: false});
    }
    const approveOpen = popState.key === popKey && popState.approve;
    const rejectOpen = popState.key === popKey && popState.reject;
    const setApproveOpen = (nextOpen: boolean) =>
        setPopState({key: popKey, approve: nextOpen, reject: nextOpen ? false : rejectOpen});
    const setRejectOpen = (nextOpen: boolean) =>
        setPopState({key: popKey, reject: nextOpen, approve: nextOpen ? false : approveOpen});

    // 按钮选中态：优先跟随打开中的浮窗（临时选择），否则改判态高亮当前结论，待审态无选中
    const pendingChoice = approveOpen ? true : rejectOpen ? false : null;
    const approveSelected = pendingChoice !== null ? pendingChoice : revising && approved;
    const rejectSelected = pendingChoice !== null ? !pendingChoice : revising && rejected;
    // 存量数据 DDL 默认即揽收失败
    const isPickupSuccess = item?.reviewScene === PopReviewScene.PICKUP_SUCCESS;
    const pickupInfo = detail?.pickupInfo;
    const pickupResult = getPopPickupResult(item?.reviewScene, pickupInfo?.isPartialPickup);
    const scanRecords = detail?.scanRecords ?? [];
    const [scanDrawerOpen, setScanDrawerOpen] = useState(false);
    const [scanKeyword, setScanKeyword] = useState('');
    // 序号取原始位置，搜索过滤后不重排
    const filteredScanRecords = scanRecords
        .map((record, index) => ({record, index}))
        .filter(({record}) =>
            (record.trackingNumber ?? '').toLowerCase().includes(scanKeyword.trim().toLowerCase())
        );

    const closeScanDrawer = () => {
        setScanDrawerOpen(false);
        setScanKeyword('');
    };

    const copyAllTrackingNumbers = async () => {
        const trackingNumbers = scanRecords
            .map(record => record.trackingNumber)
            .filter((no): no is string => Boolean(no));
        try {
            await navigator.clipboard.writeText(trackingNumbers.join('\n'));
            message.success(t('popReview.detail.scansCopied', {count: trackingNumbers.length}));
        } catch {
            message.error(t('common.messages.copyFailed'));
        }
    };

    // compact：栏内预览时间只显示 HH:mm（完整日期见顶部提交时间），查看全部抽屉仍显示完整时间
    const renderScanRecord = (record: PopReviewScanRecord, index: number, compact = false) => (
        <div key={index}>
            <span className="inline-block w-6 text-gray-400">{index + 1}</span>
            {record.trackingNumber ||
                t('popReview.detail.unscannable', {reason: record.unscannableReason || '—'})}
            {record.scannedAt != null && (
                <span className="ml-3 text-gray-500">
                    {compact
                        ? dayjs(timestampToDate(record.scannedAt)).format('HH:mm')
                        : formatEpochToDateTime(record.scannedAt)}
                </span>
            )}
        </div>
    );

    /**
     * 已选不合格原因按分类分组：「信息不全」与「信息虚假」是不同性质的判定，必须能一眼区分。
     * 顺序沿用服务端字典，不在前端另行排序，避免两处规则漂移。
     */
    const groupedSelectedReasons = React.useMemo(() => {
        const selected = new Set(item?.disqualifyReasonCodes ?? []);
        if (!selected.size) {
            return [];
        }
        const groups: ReasonGroup[] = [];
        const indexByCategory = new Map<string, number>();
        const currentLang = normalizeLang(i18n.language);

        disqualifyReasons.forEach(reason => {
            const code = reason.code;
            if (!code || !selected.has(code)) {
                return;
            }
            const categoryCode = reason.categoryCode || '';
            const categoryLabel =
                reason.categoryTexts?.find(e => e.lang === currentLang)?.text ||
                reason.categoryTexts?.find(e => e.lang === 'en')?.text ||
                reason.categoryTexts?.[0]?.text ||
                categoryCode;
            const leaf = {code, label: reasonTextByCode[code] || code};

            const existing = indexByCategory.get(categoryCode);
            if (existing !== undefined) {
                groups[existing].reasons.push(leaf);
                return;
            }
            indexByCategory.set(categoryCode, groups.length);
            groups.push({categoryCode, categoryLabel, reasons: [leaf]});
        });

        // 字典里查不到的码（如原因已下线）仍需展示，归入无分类组，避免结论凭空少一项
        const known = new Set(
            disqualifyReasons.map(r => r.code).filter(Boolean) as string[]
        );
        const orphans = [...selected].filter(c => !known.has(c));
        if (orphans.length) {
            groups.push({
                categoryCode: '__unknown__',
                categoryLabel: t('popReview.detail.uncategorizedReason'),
                reasons: orphans.map(c => ({code: c, label: reasonTextByCode[c] || c}))
            });
        }
        return groups;
    }, [item?.disqualifyReasonCodes, disqualifyReasons, reasonTextByCode, i18n.language, t]);

    // 对齐 POD：审完跳下一条，已是最后一条则关闭抽屉回列表
    const moveToNext = () => {
        if (hasNext) {
            setCurrentIndex(currentIndex + 1);
            return;
        }
        onClose();
    };

    const submit = async (
        action: PopReviewAction,
        reasonCodes?: string[],
        remark?: string,
        revision?: boolean
    ): Promise<boolean> => {
        if (!queryId) {
            return false;
        }
        const ok = await handleReview(
            queryId,
            action,
            reasonCodes,
            remark,
            // 备注语种取前端当前 locale：前端本就持有，省一次服务端语种识别
            normalizeLang(i18n.language),
            revision
        );
        if (ok) {
            setRevisingId(null);
            onReviewed?.();
            moveToNext();
        }
        return ok;
    };

    // 改判无实质变更时只提示不提交（对齐 POD）：合格不带原因与备注，合格改合格恒无变更；
    // 不合格改不合格需原因码集合或备注原文有变
    const notifyNoChange = () => message.info(t('podReview.messages.noChangesDetected'));
    const isUnchangedReject = (codes: string[], remark: string) => {
        const original = item?.disqualifyReasonCodes ?? [];
        return (
            rejected &&
            codes.length === original.length &&
            codes.every(code => original.includes(code)) &&
            remark.trim() === (item?.reviewRemark?.text ?? '').trim()
        );
    };

    const driverName =
        item?.driverName ||
        (item?.driverId ? pickupDriverNameById[item.driverId] || item.driverId : undefined);

    // POP 照片在前，BOL 接在后面并单独标注
    const photos = [
        ...(detail?.popPhotoUrls ?? []).map((url, index) => ({url, label: `${index + 1}`})),
        ...(detail?.bolPhotoUrl ? [{url: detail.bolPhotoUrl, label: t('popReview.detail.bol')}] : [])
    ];

    // 按当前 locale 取译文，无命中回落原文（译文异步补且允许丢失）
    const reviewRemarkText = pickDisplayText(item?.reviewRemark, normalizeLang(i18n.language));

    // 优先用揽收侧直接给出的站点名（不依赖是否选过 Hub）；
    // 其次用筛选区下拉的 id→name 映射；都没有才回落显示 ID
    const siteName =
        detail?.pickupInfo?.siteName ||
        (item?.pickupSiteId
            ? pickupSiteNameById[item.pickupSiteId] || item.pickupSiteId
            : undefined);

    const parcelUnitText =
        pickupInfo?.actualParcelUnit != null && PARCEL_UNIT_I18N[pickupInfo.actualParcelUnit]
            ? t(`enum.packageUnitFilterOptions.${PARCEL_UNIT_I18N[pickupInfo.actualParcelUnit]}`)
            : '';
    // 件数版式照原型：部分揽收合并为「实际 X / 预约 Y 单位」，否则「X 单位」
    const parcelCountText =
        item?.actualParcelCount == null
            ? undefined
            : pickupResult === PopPickupResult.PARTIAL && pickupInfo?.expectedParcelCount != null
              ? t('popReview.detail.actualVsExpectedCount', {
                    actual: item.actualParcelCount,
                    expected: pickupInfo.expectedParcelCount,
                    unit: parcelUnitText
                }).trim()
              : `${item.actualParcelCount} ${parcelUnitText}`.trim();

    // 抽屉 header 固定不随内容滚动，审核操作放在这里即为吸顶栏
    const header = (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
                <ArrowLeftOutlined
                    className="cursor-pointer hover:text-gray-600"
                    onClick={onClose}
                />
                <span className="text-lg font-medium">{item?.appointmentNumber || ''}</span>
                {item && (
                    <Tag color={isPickupSuccess ? 'green' : 'orange'}>
                        {t(`popReview.scene.${isPickupSuccess ? PopReviewScene.PICKUP_SUCCESS : PopReviewScene.PICKUP_FAILED}`)}
                    </Tag>
                )}
                {item && (
                    <span className="text-sm font-normal text-gray-500">
                        {t('popReview.detail.submittedAt')}: {formatEpochToDateTime(item.submittedAt) || '—'}
                        <span className="mx-3">|</span>
                        {t('popReview.detail.driver')}: {driverName || '—'}
                    </span>
                )}
                {isBatchMode && (
                    <div className="ml-auto flex items-center gap-3 text-sm font-normal">
                        <span>
                            {t('popReview.detail.progress', {
                                current: currentIndex + 1,
                                total: reviewIds.length
                            })}
                        </span>
                        {showNoMore ? (
                            <span className="text-gray-400">{t('popReview.detail.noMore')}</span>
                        ) : (
                            <Button size="small" onClick={moveToNext}>
                                {t('popReview.detail.skip')} →
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {item && (
                <div className="flex items-center gap-3 text-sm font-normal">
                    <span className="text-gray-500">{t('popReview.detail.reviewResult')}:</span>
                    <Tag color={STATUS_COLOR[item.status] || 'default'}>
                        {t(`popReview.enum.status.${item.status}`)}
                    </Tag>
                    {groupedSelectedReasons.length > 0 && (
                        <div className="flex flex-wrap gap-1 min-w-0">
                            {groupedSelectedReasons.flatMap(group =>
                                group.reasons.map(reason => (
                                    <Tag key={reason.code} color="red" className="!m-0">
                                        {group.categoryLabel} / {reason.label}
                                    </Tag>
                                ))
                            )}
                        </div>
                    )}
                    {finished && item.reviewerId && (
                        <span className="text-gray-500 max-w-[360px] truncate" title={reviewRemarkText}>
                            {item.reviewer?.name || item.reviewerId} · {formatEpochToDateTime(item.reviewedAt)}
                            {reviewRemarkText && ` · ${reviewRemarkText}`}
                        </span>
                    )}
                    <div className="ml-auto flex gap-3">
                        {finished && !revising ? (
                            // 看结论属于查看权限，不包 OPERATE 资源码
                            <>
                                <Button disabled className={finishedButtonClass(approved, true)}>
                                    {t('popReview.detail.approve')}
                                </Button>
                                <Button disabled className={finishedButtonClass(rejected, false)}>
                                    {t('popReview.detail.reject')}
                                </Button>
                                {revisable && (
                                    <HasResource resourceCodes={[ResourceCode.cs.CS_POP_REVIEW_OPERATE]}>
                                        <Button
                                            className={REVIEW_BUTTON_BASE}
                                            onClick={() => setRevisingId(queryId)}
                                        >
                                            {t('popReview.detail.revise')}
                                        </Button>
                                    </HasResource>
                                )}
                            </>
                        ) : (
                            <HasResource resourceCodes={[ResourceCode.cs.CS_POP_REVIEW_OPERATE]}>
                                <div className="flex gap-3">
                                    {/* 合格无原因可填，确认浮窗即二次确认（不合格的二次确认由原因浮窗承担）；
                                        卡片样式与不合格原因浮窗保持同一视觉语言 */}
                                    <Popover
                                        trigger="click"
                                        placement="bottomRight"
                                        arrow={false}
                                        open={approveOpen}
                                        onOpenChange={nextOpen => {
                                            if (nextOpen && revising && approved) {
                                                notifyNoChange();
                                                return;
                                            }
                                            setApproveOpen(nextOpen);
                                        }}
                                        styles={{body: {padding: 0, borderRadius: 12, overflow: 'hidden'}}}
                                        content={
                                            <div className="w-[380px] flex flex-col">
                                                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5">
                                                    <span className="text-[15px] font-bold min-w-0">
                                                        {t('popReview.detail.confirmApprove')}
                                                    </span>
                                                    {revising && (
                                                        <span className="flex-shrink-0 whitespace-nowrap text-[11.5px] text-orange-600 bg-orange-50 border border-orange-200 rounded-md px-2 py-1">
                                                            {t('podReview.detail.editingOverwriteHint')}
                                                        </span>
                                                    )}
                                                    <CloseOutlined
                                                        className="ml-auto flex-shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer"
                                                        onClick={() => setApproveOpen(false)}
                                                    />
                                                </div>
                                                <div className="px-5 py-3 text-[13px] text-gray-600 leading-6">
                                                    {revising
                                                        ? t('popReview.detail.approveReviseContent')
                                                        : t('popReview.detail.approveContent')}
                                                </div>
                                                <div className="px-4 py-3 border-t border-gray-100 flex items-center gap-2">
                                                    <Button className="ml-auto" onClick={() => setApproveOpen(false)}>
                                                        {t('common.cancel')}
                                                    </Button>
                                                    <Button
                                                        type="primary"
                                                        loading={submitting}
                                                        onClick={async () => {
                                                            await submit(PopReviewAction.APPROVE, undefined, undefined, revising);
                                                            setApproveOpen(false);
                                                        }}
                                                    >
                                                        {revising
                                                            ? t('podReview.actions.saveChanges')
                                                            : t('common.confirm')}
                                                    </Button>
                                                </div>
                                            </div>
                                        }
                                    >
                                        <Button
                                            loading={submitting}
                                            className={verdictButtonClass(true, approveSelected)}
                                        >
                                            {t('popReview.detail.approve')}
                                        </Button>
                                    </Popover>
                                    {/* key 绑定当前单与是否改判：切单/进入改判时重置浮窗内已选原因与备注 */}
                                    <PopReviewRejectPopover
                                        key={popKey}
                                        open={rejectOpen}
                                        onOpenChange={setRejectOpen}
                                        reasonOptions={reasonOptions}
                                        submitting={submitting}
                                        // 改判预填当前结论的原因与备注原文，审核员在其上修改
                                        initialCodes={revising ? item.disqualifyReasonCodes ?? undefined : undefined}
                                        initialRemark={revising ? item.reviewRemark?.text : undefined}
                                        revising={revising}
                                        onSubmit={async (codes, remark) => {
                                            if (revising && isUnchangedReject(codes, remark)) {
                                                notifyNoChange();
                                                return false;
                                            }
                                            return submit(PopReviewAction.REJECT, codes, remark, revising);
                                        }}
                                    >
                                        <Button
                                            loading={submitting}
                                            className={verdictButtonClass(false, rejectSelected)}
                                        >
                                            {t('popReview.detail.reject')}
                                        </Button>
                                    </PopReviewRejectPopover>
                                    {revising && (
                                        <Button onClick={() => setRevisingId(null)} className={REVIEW_BUTTON_BASE}>
                                            {t('popReview.detail.cancelRevise')}
                                        </Button>
                                    )}
                                </div>
                            </HasResource>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <Drawer
            title={header}
            placement="right"
            // 近全屏：照片是审核的核心依据，需要足够版面（与 podReview 一致）
            width="calc(100vw - 80px)"
            onClose={onClose}
            open={open}
            closable={false}
            destroyOnClose
            styles={{body: {padding: 0}, header: {borderBottom: '1px solid #f0f0f0'}}}
        >
            <Spin spinning={loading}>
                {!item ? (
                    <div className="px-8 py-6">
                        <Empty description={t('popReview.detail.notFound')} />
                    </div>
                ) : (
                    <div className="h-full bg-white">
                    {/* 只展示 PRD 列出的字段，避免审核员在无关信息里找判断依据 */}
                    <div className="px-6 py-2">
                        <div className="border border-gray-200 rounded-lg bg-white">
                            <div className="flex items-center px-6 py-3 border-b border-gray-100">
                                <span className="text-gray-500 text-sm w-[104px] flex-shrink-0">
                                    {t('popReview.detail.appointmentLabel')}
                                </span>
                                <span className="text-purple-600 font-medium">
                                    {item.appointmentNumber || '—'}
                                </span>
                            </div>

                            <Row className="px-6 py-4">
                                {/* ---------- 站点信息 ---------- */}
                                <Col span={7} className="pr-6">
                                    <div className="mb-3 font-medium">
                                        {t('popReview.detail.siteSection')}
                                    </div>
                                    {/* 站点名突出展示（原型无标签），站点ID 单独成行 */}
                                    <div className="text-[15px] font-semibold leading-6 mb-2 break-words">
                                        {siteName || '—'}
                                    </div>
                                    <LabeledText
                                        label={t('popReview.detail.siteId')}
                                        value={item.pickupSiteId}
                                    />
                                    <div className="flex text-sm leading-6 mb-2">
                                        <span className="text-gray-500 flex-shrink-0 w-[104px] mr-3">
                                            {t('popReview.detail.longTermPickup')}:
                                        </span>
                                        <span className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                                            {pickupInfo?.isLongTermPickup == null ? (
                                                '—'
                                            ) : (
                                                <Tag
                                                    color={pickupInfo.isLongTermPickup ? 'purple' : 'default'}
                                                    className="m-0"
                                                >
                                                    {t(pickupInfo.isLongTermPickup ? 'common.yes' : 'common.no')}
                                                </Tag>
                                            )}
                                            {item.pickupDetail?.source && (
                                                <span className="text-gray-500">
                                                    {t('popReview.table.source')}:{' '}
                                                    {t(`popReview.enum.source.${item.pickupDetail.source}`, {
                                                        defaultValue: item.pickupDetail.source
                                                    })}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <LabeledText
                                        label={t('popReview.detail.pickupAddress')}
                                        value={joinAddress(
                                            detail?.pickupInfo?.address,
                                            detail?.pickupInfo?.zipcode
                                        )}
                                    />
                                    <LabeledText
                                        label={t('popReview.detail.contactName')}
                                        value={joinContact(
                                            detail?.pickupInfo?.contactName,
                                            detail?.pickupInfo?.contactPhone
                                        )}
                                    />
                                </Col>

                                {/* ---------- 揽收信息（状态挂标题徽标，按场景展示） ---------- */}
                                <Col span={6} className="px-6 border-l border-gray-100">
                                    <div className="mb-3 font-medium flex items-center gap-2">
                                        {t('popReview.detail.pickupSection')}
                                        {pickupResult ? (
                                            <Tag
                                                color={PICKUP_RESULT_TAG_COLOR[pickupResult]}
                                                className="m-0"
                                            >
                                                {t(`popReview.enum.pickupResult.${pickupResult}`)}
                                            </Tag>
                                        ) : (
                                            <span className="text-gray-400 font-normal">—</span>
                                        )}
                                    </div>
                                    {isPickupSuccess ? (
                                        <>
                                            <LabeledText
                                                label={t('popReview.detail.actualParcelCount')}
                                                value={parcelCountText}
                                            />
                                            {pickupResult === PopPickupResult.PARTIAL && (
                                                <LabeledText
                                                    label={t('popReview.detail.partialPickupReason')}
                                                    value={
                                                        pickupInfo?.partialPickupReason != null &&
                                                        PARTIAL_REASON_I18N[pickupInfo.partialPickupReason]
                                                            ? t(
                                                                  `pickup.progress.partialPickup.${
                                                                      PARTIAL_REASON_I18N[
                                                                          pickupInfo.partialPickupReason
                                                                      ]
                                                                  }`
                                                              )
                                                            : undefined
                                                    }
                                                />
                                            )}
                                            <LabeledText
                                                label={t('popReview.detail.parcelUnit')}
                                                value={parcelUnitText || undefined}
                                            />
                                        </>
                                    ) : (
                                        <LabeledText
                                            label={t('popReview.detail.pickupFailureReasonCode')}
                                            // 展示原因文案而非码值；未收录的码回落显示码本身
                                            value={
                                                item.pickupFailureReasonCode
                                                    ? getPickupFailureReasonText(
                                                          item.pickupFailureReasonCode
                                                      )
                                                    : undefined
                                            }
                                        />
                                    )}
                                    <LabeledText
                                        label={t('popReview.detail.gps')}
                                        // 缺失定位本身即「疑似虚假」信号，须明确提示而非留空
                                        value={
                                            item.pickupGpsLatitude && item.pickupGpsLongitude
                                                ? `${item.pickupGpsLatitude}, ${item.pickupGpsLongitude}`
                                                : t('popReview.detail.gpsUnavailable')
                                        }
                                    />
                                    <LabeledText
                                        label={t('popReview.detail.distance')}
                                        value={
                                            item.pickupSiteDistanceMeters == null
                                                ? t('popReview.table.distanceUnavailable')
                                                : t('popReview.detail.distanceMeters', {
                                                      value: item.pickupSiteDistanceMeters
                                                  })
                                        }
                                    />
                                </Col>

                                {/* ---------- 扫码运单号（独立栏；失败场景无运单显示「—」） ---------- */}
                                <Col span={6} className="px-6 border-l border-gray-100">
                                    <div className="mb-3 font-medium">
                                        {t('popReview.detail.scanRecords')}
                                        {scanRecords.length > 0 && (
                                            <span className="ml-2 text-xs font-normal text-gray-400">
                                                {t('popReview.detail.scanCountBadge', {
                                                    count: scanRecords.length
                                                })}
                                            </span>
                                        )}
                                    </div>
                                    {scanRecords.length ? (
                                        <div className="text-sm leading-7">
                                            {scanRecords
                                                .slice(0, SCAN_PREVIEW_COUNT)
                                                .map((record, index) =>
                                                    renderScanRecord(record, index, true)
                                                )}
                                            {scanRecords.length > SCAN_PREVIEW_COUNT && (
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    className="!p-0"
                                                    onClick={() => setScanDrawerOpen(true)}
                                                >
                                                    {t('popReview.detail.viewAllScans', {
                                                        count: scanRecords.length
                                                    })}
                                                </Button>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-sm">—</span>
                                    )}
                                </Col>

                                {/* ---------- 审核信息（只读；审核操作在顶部栏） ---------- */}
                                <Col span={5} className="pl-6 border-l border-gray-100">
                                    <div className="mb-3 font-medium">
                                        {t('popReview.detail.reviewSection')}
                                    </div>
                                    <LabeledText
                                        label={t('popReview.detail.reviewer')}
                                        // 后端 DataLoader 回查 account 得姓名；查不到回落显示 ID；待审核显示「—」
                                        value={
                                            item.reviewerId
                                                ? item.reviewer?.name || item.reviewerId
                                                : undefined
                                        }
                                    />
                                    <div className="flex text-sm leading-6 mb-2">
                                        <span className="text-gray-500 flex-shrink-0 w-[104px] mr-3">
                                            {/* 原因标签按场景切换：失败单是「失败原因」，成功单是「不合格原因」 */}
                                            {isPickupSuccess
                                                ? t('popReview.table.disqualifyReasons')
                                                : t('popReview.detail.failureReason')}
                                            :
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            {groupedSelectedReasons.length ? (
                                                <GroupedReasons groups={groupedSelectedReasons} />
                                            ) : (
                                                <span>—</span>
                                            )}
                                        </div>
                                    </div>
                                    <LabeledText
                                        label={t('popReview.detail.reviewRemark')}
                                        value={reviewRemarkText}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* 照片：≤3 张三等分占满整行（对齐 podReview），>3 张每张至少 300px 自动换行并拉伸填满；左上角标序号，BOL 接在 POP 照片后 */}
                    <div className="px-8 py-4 bg-gray-50 border-t">
                        <div className="mb-3 font-medium">
                            {t('popReview.detail.photoSection')}
                            {photos.length > 0 && (
                                <span className="ml-2 text-xs font-normal text-gray-400">
                                    {t('popReview.detail.photoCountHint', {count: photos.length})}
                                </span>
                            )}
                        </div>
                        <div
                            className={`grid gap-4 ${photos.length <= 3 ? 'grid-cols-3' : 'grid-cols-[repeat(auto-fill,minmax(300px,1fr))]'}`}
                        >
                            {photos.length > 0 ? (
                                <Image.PreviewGroup>
                                    {photos.map(photo => (
                                        <div
                                            key={photo.url}
                                            className={`${PHOTO_CELL_CLASS} hover:border-purple-400 transition-colors`}
                                        >
                                            <Image
                                                src={photo.url}
                                                alt={`POP ${photo.label}`}
                                                rootClassName="!block w-full h-full"
                                                className="object-cover w-full h-full"
                                                preview={{
                                                    mask: (
                                                        <span className="text-white text-sm font-medium">
                                                            {t('popReview.detail.clickToPreview')}
                                                        </span>
                                                    )
                                                }}
                                            />
                                            <span className="absolute top-2 left-2 px-1.5 rounded bg-black/60 text-white text-xs leading-5">
                                                {photo.label}
                                            </span>
                                        </div>
                                    ))}
                                </Image.PreviewGroup>
                            ) : (
                                // 无照片时用相机图标占位（对齐 podReview），保持版面结构稳定
                                [1, 2, 3].map(index => (
                                    <div
                                        key={index}
                                        className={`${PHOTO_CELL_CLASS} flex items-center justify-center`}
                                    >
                                        <CameraOutlined className="text-8xl text-gray-400" />
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                    </div>
                )}
            </Spin>
            <Drawer
                open={scanDrawerOpen}
                width={420}
                title={t('popReview.detail.scanRecordsTitle', {count: scanRecords.length})}
                extra={
                    <Button size="small" onClick={copyAllTrackingNumbers}>
                        {t('popReview.detail.copyAllScans')}
                    </Button>
                }
                onClose={closeScanDrawer}
            >
                <Input
                    allowClear
                    placeholder={t('popReview.detail.searchTrackingNumber') as string}
                    value={scanKeyword}
                    onChange={e => setScanKeyword(e.target.value)}
                />
                <div className="mt-3 text-sm leading-7">
                    {filteredScanRecords.map(({record, index}) => renderScanRecord(record, index))}
                </div>
            </Drawer>
        </Drawer>
    );
};

export default PopReviewDetailDrawer;
